// 급여관리 PRO 매트릭스의 그룹·컬럼 정의. 목업 1.화면목업_clean.html 의 `G` 배열과 동일 구성.
// 애매하면 목업이 기준이다.

import { TaskCell } from "models/payrollManagement.model";

// 노출주기 코드
//   M  = 매월
//   H  = 반기 (7·1월 지급분 화면에서만)
//   Y3 = 다음연도 3월 지급분 화면에서만
//   Y2 = 다음연도 2월 지급분 화면에서만
export type CycleCode = "M" | "H" | "Y3" | "Y2";

export interface GridColumn {
  id: string;
  label: string;
  // 넓은 칸 (칸 이름이 길어 두 줄로 표시)
  wide?: boolean;
  // 최소 폭(px) 직접 지정 (없으면 wide 60 / 기본 34)
  minWidth?: number;
  // 간이/정식 구분 표식
  formTag?: string;
  // 노출주기 (없으면 매월 상시 노출)
  cycle?: CycleCode;
  // 담당자 수동 체크 대상이면 대응하는 TaskCell
  manualCell?: TaskCell;
}

export interface GridGroup {
  id: string;
  name: string;
  // 헤더 두 줄 표기 (없으면 name)
  headLabel?: string;
  // 그룹 법정기한 = (이 지급월을 귀속월로 보고) 다음 달 dueDay일.
  //  원천세 신고·납부서·발송 → 다음 달 10일 / 공단업무 → 다음 달 15일.
  //  노출주기(cycle) 컬럼이 있는 그룹은 이 값 대신 cycle 기준으로 계산한다.
  dueDay?: number;
  htip?: string;
  cols: GridColumn[];
}

export const GROUPS: GridGroup[] = [
  {
    id: "pay",
    name: "급여자료 입력",
    cols: [
      { id: "earned", label: "근로" },
      { id: "daily", label: "일용" },
      { id: "biz", label: "사업" },
      { id: "ret", label: "퇴직" },
      { id: "etc", label: "기타" }
    ]
  },
  {
    id: "tx",
    name: "세무사랑 전송",
    headLabel: "세무사랑\n전송",
    cols: [{ id: "tx", label: "전송" }]
  },
  {
    id: "file",
    name: "원천세 신고",
    dueDay: 10,
    cols: [{ id: "file", label: "신고", manualCell: "WITHHOLDING_TAX_FILED" }]
  },
  {
    id: "note",
    name: "납부서 및 접수증",
    dueDay: 10,
    cols: [
      { id: "n_ed", label: "근로/일용" },
      { id: "n_biz", label: "사업" },
      { id: "n_ret", label: "퇴직" },
      { id: "n_etc", label: "기타" },
      { id: "n_loc", label: "지방세" },
      { id: "rc_wt", label: "접수증" },
      // 목업에 없던 추가 컬럼 — 위 6칸 중 어디에도 해당하지 않는 납부서 파일이 있는지 플래그
      { id: "etc_notice", label: "ETC" }
    ]
  },
  {
    id: "disp",
    name: "발송",
    dueDay: 10,
    // 접으면 발송 여부만, 펼치면 왼쪽에 알림톡 수신 번호 칸이 나온다.
    cols: [
      { id: "phone", label: "수신 번호", minWidth: 108 },
      { id: "dispatch", label: "발송" }
    ]
  },
  {
    id: "ins",
    name: "공단업무",
    dueDay: 15,
    cols: [
      {
        id: "i_day",
        label: "일용",
        wide: true,
        manualCell: "INSURANCE_DAILY_WORK_REPORT"
      },
      { id: "i_acq", label: "취득", manualCell: "INSURANCE_ACQUISITION" },
      { id: "i_loss", label: "상실", manualCell: "INSURANCE_LOSS" }
    ]
  },
  {
    id: "stmtM",
    name: "매월 지급명세서",
    headLabel: "매월\n지급명세서",
    cols: [
      {
        id: "s_day",
        label: "일용",
        cycle: "M",
        manualCell: "MONTHLY_STATEMENT_DAILY"
      },
      {
        id: "q_biz",
        label: "사업",
        formTag: "간이",
        cycle: "M",
        manualCell: "MONTHLY_STATEMENT_BUSINESS_SIMPLE"
      },
      {
        id: "q_etc",
        label: "기타",
        formTag: "간이",
        cycle: "M",
        manualCell: "MONTHLY_STATEMENT_OTHER_SIMPLE"
      }
    ]
  },
  {
    id: "stmtY",
    name: "연간 지급명세서",
    headLabel: "연간\n지급명세서",
    htip: "연간 지급명세서 일정 — 1·7월 말일 근로(간이) · 2월 말일 기타소득 · 다음연도 3월 10일 근로·사업·퇴직",
    cols: [
      {
        id: "q_ear",
        label: "근로",
        formTag: "간이",
        cycle: "H",
        manualCell: "ANNUAL_STATEMENT_EARNED_SIMPLE"
      },
      {
        id: "s_ear",
        label: "근로",
        cycle: "Y3",
        manualCell: "ANNUAL_STATEMENT_EARNED"
      },
      {
        id: "s_biz",
        label: "사업",
        cycle: "Y3",
        manualCell: "ANNUAL_STATEMENT_BUSINESS"
      },
      {
        id: "s_ret",
        label: "퇴직",
        cycle: "Y3",
        manualCell: "ANNUAL_STATEMENT_RETIREMENT"
      },
      {
        id: "s_etc",
        label: "기타",
        cycle: "Y2",
        manualCell: "ANNUAL_STATEMENT_OTHER"
      }
    ]
  }
];

// 컬럼 id → GridColumn (빠른 조회용)
export const COLUMN_BY_ID: Record<string, GridColumn> = GROUPS.reduce(
  (acc, g) => {
    g.cols.forEach((c) => {
      acc[c.id] = c;
    });
    return acc;
  },
  {} as Record<string, GridColumn>
);

// 1칸 그룹은 접히지 않는다 (접으면 그룹 요약 dot이 그려져 클릭 토글이 불가능)
export const COLLAPSIBLE_GROUP_IDS = GROUPS.filter(
  (g) => g.cols.length > 1
).map((g) => g.id);

// "납부서 및 접수증" 그룹 컬럼 — 클릭하면 그 거래처의 납부서 목록 팝업을 연다.
export const NOTE_GROUP_COLUMN_IDS = new Set(
  (GROUPS.find((g) => g.id === "note")?.cols ?? []).map((c) => c.id)
);

// ---------------------------------------------------------------------------
// 노출주기 판정
// ---------------------------------------------------------------------------
const lastDay = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

// 이 지급월 화면에 해당 주기 컬럼이 노출되는지
export function isColumnInScope(
  cycle: CycleCode | undefined,
  viewMonth: number
) {
  if (!cycle || cycle === "M") return true;
  if (cycle === "H") return viewMonth === 7 || viewMonth === 1;
  if (cycle === "Y3") return viewMonth === 3;
  if (cycle === "Y2") return viewMonth === 2;
  return false;
}

// 노출주기 코드 → 이 화면에서의 실제 마감일 표기
export function cycleDueText(
  cycle: CycleCode | undefined,
  viewYear: number,
  viewMonth: number
): string | null {
  if (!cycle) return null;
  if (cycle === "M") {
    const y = viewMonth === 12 ? viewYear + 1 : viewYear;
    const m = viewMonth === 12 ? 1 : viewMonth + 1;
    return `${m}/${lastDay(y, m)}`;
  }
  if (cycle === "H") return viewMonth === 7 ? "7/31" : "1/31";
  if (cycle === "Y3") return "3/10";
  if (cycle === "Y2") return `2/${lastDay(viewYear, 2)}`;
  return null;
}

// 그룹 헤더에 찍을 마감일
export function groupDueText(
  group: GridGroup,
  viewYear: number,
  viewMonth: number
): string | null {
  if (!group.cols.some((c) => c.cycle)) {
    if (!group.dueDay) return null;
    // 이 지급월을 귀속월로 보고 다음 달 dueDay일 (당월 신고기준 기준. 12월 → 1월)
    const m = viewMonth === 12 ? 1 : viewMonth + 1;
    return `${m}/${group.dueDay}`;
  }
  const live = group.cols.filter((c) => isColumnInScope(c.cycle, viewMonth));
  if (!live.length) return null;
  const dues = Array.from(
    new Set(live.map((c) => cycleDueText(c.cycle, viewYear, viewMonth)))
  );
  return dues[0] || null;
}
